import { AlertTriangle, LoaderCircle, MapPin } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  Infeasibility,
  ResolutionOption,
} from "../hook/useGenerateRosterMutation";
import type { ResolutionChange } from "@/types/roster-infeasibility";
import { cn } from "@/lib/utils";

type RosterCreateBlockingInfeasibilityAlertProps = {
  readonly infeasibility: Infeasibility | null;
  readonly onClose: () => void;
  /** 유저가 해결 옵션(auto_apply)을 고르면 그 delta 로 다시 생성. 없으면 버튼 미표시. */
  readonly onSelectResolution?: (
    option: ResolutionOption
  ) => void | Promise<void>;
  readonly onNavigateFix?: (option: ResolutionOption) => void;
};

function getChangeText(change: ResolutionChange): string {
  const label = change.label_ko ?? change.config_key;
  const from = change.from == null ? null : String(change.from);
  const to =
    change.to == null
      ? change.suggested_value == null
        ? null
        : String(change.suggested_value)
      : String(change.to);

  if (from && to) return `${label} ${from} → ${to}`;
  if (to) return `${label} ${to}`;
  return label;
}

export function RosterCreateBlockingInfeasibilityAlert({
  infeasibility,
  onClose,
  onSelectResolution,
  onNavigateFix,
}: RosterCreateBlockingInfeasibilityAlertProps) {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pendingResolution, setPendingResolution] =
    useState<ResolutionOption | null>(null);
  const [isApplyingResolution, setIsApplyingResolution] = useState(false);

  if (!infeasibility) return null;

  const options = infeasibility.resolution_options ?? [];
  const hasNavigation =
    !!onNavigateFix &&
    options.some(
      (option) =>
        option.fix?.mode === "manual_navigate" &&
        option.fix.where === "nurse.monthly_limit"
    );
  const navigationOnly =
    hasNavigation &&
    options.every(
      (option) =>
        option.fix?.mode === "manual_navigate" &&
        option.fix.where === "nurse.monthly_limit"
    );
  const summary = infeasibility.summary_message_ko?.trim() ?? "";
  const causes = (infeasibility.causes ?? []).filter((cause) => cause.fix);
  const issues = infeasibility.preflight_issues;
  const ui = infeasibility.ui_text ?? {};
  const t = (key: keyof typeof ui, fallback: string) =>
    (ui[key]?.trim?.() || fallback) as string;
  const optionsHeader = `조정 방법 선택 ${options.length}가지`;

  return (
    <Dialog
      open={!!infeasibility}
      onOpenChange={(open) => {
        if (!open) setShowCloseConfirm(true);
      }}
    >
      <DialogContent className="max-h-[calc(100vh-80px)] w-[760px] max-w-[calc(100vw-48px)] gap-0 overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
        <DialogHeader className="border-b border-border px-7 py-6 text-left">
          <DialogTitle className="break-keep text-[22px] font-bold leading-tight text-foreground">
            {t("title", "근무표 생성 조건을 조정해야 합니다")}
          </DialogTitle>
          <DialogDescription className="mt-1.5 max-w-[600px] break-keep text-sm leading-6 text-muted-foreground">
            {navigationOnly
              ? "아래 카드를 선택해 근무자 관리에서 해당 월의 나이트 개수를 수정한 후 다시 생성해 주세요."
              : t(
                  "description",
                  "현재 조건에서는 생성이 어렵습니다. 아래 조정안을 선택하면 조건을 반영해 다시 생성합니다."
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(100vh-260px)] overflow-y-auto px-7 py-6">
          {summary && (
            <section className="rounded-xl border border-[#FCA5A5] bg-[#FFF5F5] px-4 py-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                  <AlertTriangle className="size-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-destructive">
                    생성이 막힌 조건
                  </p>
                  <p className="mt-0.5 break-keep text-sm leading-6 text-foreground">
                    {summary}
                  </p>
                </div>
              </div>
            </section>
          )}

          {options.length > 0 && (
            <section className="mt-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-foreground">
                    {optionsHeader}
                  </h3>
                  <p className="mt-1 break-keep text-sm leading-6 text-muted-foreground">
                    {hasNavigation
                      ? navigationOnly
                        ? "카드를 선택하면 해당 월의 근무자 관리로 이동합니다."
                        : "자동 적용은 확인 후 재생성하고, 직접 수정은 해당 화면으로 이동합니다."
                      : "조정된 설정을 반영하고, 확인 후 다시 생성합니다."}
                  </p>
                </div>
              </div>

              <ul className="mt-4 space-y-3">
                {options.map((option) => {
                  const canAuto =
                    option.fix?.mode === "auto_apply" && !!onSelectResolution;
                  const canNavigate =
                    !canAuto &&
                    option.fix?.mode === "manual_navigate" &&
                    option.fix.where === "nurse.monthly_limit" &&
                    !!onNavigateFix;
                  const canClick = canAuto || canNavigate;
                  const handleSelect = () => {
                    if (canAuto) setPendingResolution(option);
                    else if (canNavigate) onNavigateFix?.(option);
                  };
                  return (
                    <li key={option.option_id}>
                      <article
                        role={canClick ? "button" : undefined}
                        tabIndex={canClick ? 0 : undefined}
                        onClick={canClick ? handleSelect : undefined}
                        onKeyDown={
                          canClick
                            ? (event) => {
                                if (
                                  event.key !== "Enter" &&
                                  event.key !== " "
                                ) {
                                  return;
                                }
                                event.preventDefault();
                                handleSelect();
                              }
                            : undefined
                        }
                        className={cn(
                          "rounded-xl border border-border bg-white px-5 py-4 shadow-sm transition duration-150",
                          canClick
                            ? "cursor-pointer hover:border-sig/40 hover:shadow-[0_8px_28px_rgba(0,114,255,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/25"
                            : "cursor-default"
                        )}
                      >
                        <div className="flex min-w-0 items-start">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="break-keep text-[17px] font-bold leading-tight text-foreground">
                                {option.title_ko}
                              </h4>
                              {option.verified && (
                                <span
                                  className="inline-flex h-6 shrink-0 items-center rounded-full border px-2.5 text-xs font-bold leading-none"
                                  style={{
                                    backgroundColor: "#DBEAFE",
                                    borderColor: "#BFDBFE",
                                    color: "#0B5ED7",
                                  }}
                                >
                                  확인됨
                                </span>
                              )}
                            </div>

                            {option.fix?.how_ko && (
                              <p className="mt-2 break-keep text-sm leading-6 text-foreground">
                                {option.fix.how_ko}
                              </p>
                            )}

                            {((option.changes && option.changes.length > 0) ||
                              option.trade_off_ko) && (
                              <div className="mt-3 rounded-lg bg-light-muted px-3 py-2.5">
                                {option.changes &&
                                  option.changes.length > 0 && (
                                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                                      {option.changes.map((change) => (
                                        <span
                                          key={`${option.option_id}-${change.config_key}`}
                                          className="text-sm font-bold text-foreground"
                                        >
                                          {getChangeText(change)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                {option.trade_off_ko && (
                                  <p className="mt-2 break-keep text-[13px] font-medium leading-5 text-muted-foreground">
                                    {option.trade_off_ko}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-4 border-t border-border/70 pt-3">
                          {option.fix?.where_label_ko ? (
                            <p className="flex min-w-0 items-start gap-1.5 text-xs leading-5 text-muted-foreground">
                              <MapPin
                                className="mt-0.5 size-3.5 shrink-0"
                                aria-hidden="true"
                              />
                              <span className="break-keep">
                                {option.fix.where_label_ko}
                              </span>
                            </p>
                          ) : (
                            <p className="break-keep text-xs leading-5 text-muted-foreground">
                              조정된 설정으로 다시 생성합니다.
                            </p>
                          )}

                          {!canAuto && (
                            <span
                              className={cn(
                                "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                                canNavigate
                                  ? "bg-blue-50 text-sig"
                                  : "bg-light-muted text-muted-foreground"
                              )}
                            >
                              {canNavigate
                                ? "근무자 관리에서 수정 ›"
                                : "직접 수정 필요"}
                            </span>
                          )}
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {causes.length > 0 && (
            <section className="mt-6">
              <h3 className="text-base font-bold text-foreground">
                직접 확인할 항목
              </h3>
              <div className="mt-3 rounded-xl bg-light-muted px-4 py-3">
                <ul className="space-y-3">
                  {causes.map((cause, index) => {
                    const message = cause.human_message_ko?.trim();
                    const showMessage = !!message && message !== summary;
                    return (
                      <li
                        key={`${cause.reason_code ?? "cause"}-${index}`}
                        className="text-sm leading-6 text-muted-foreground"
                      >
                        {showMessage && (
                          <p className="break-keep font-medium text-foreground">
                            {cause.human_message_ko}
                          </p>
                        )}
                        {cause.fix?.where_label_ko && (
                          <p className="mt-1 flex items-start gap-1.5 break-keep">
                            <MapPin
                              className="mt-0.5 size-3.5 shrink-0"
                              aria-hidden="true"
                            />
                            {cause.fix.where_label_ko}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          )}

          {options.length === 0 && causes.length === 0 && issues.length > 0 && (
            <section className="mt-7">
              <h3 className="text-base font-bold text-foreground">
                확인된 문제 {issues.length}건
              </h3>
              <ul className="mt-3 max-h-[220px] space-y-2 overflow-y-auto rounded-xl border border-border bg-white p-4">
                {issues.map((issue, index) => (
                  <li
                    key={`${issue.reason_code}-${index}`}
                    className="break-keep text-sm leading-7 text-muted-foreground"
                  >
                    {issue.human_message_ko}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {options.length === 0 &&
            infeasibility.fix_suggestions_ko.length > 0 && (
              <section className="mt-7">
                <h3 className="text-base font-bold text-foreground">
                  조정 제안
                </h3>
                <ul className="mt-3 space-y-2 rounded-xl bg-light-muted p-4">
                  {infeasibility.fix_suggestions_ko.map((suggestion, index) => (
                    <li
                      key={`${suggestion}-${index}`}
                      className="break-keep text-sm leading-7 text-muted-foreground"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </section>
            )}
        </div>

        <DialogFooter className="border-t border-border px-8 py-5">
          <Button
            type="button"
            onClick={() => setShowCloseConfirm(true)}
            className="h-11 min-w-[120px] rounded-full bg-white px-7 text-sm font-bold text-foreground ring-1 ring-inset ring-border hover:bg-light-muted"
          >
            {t("close_button", "닫기")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog
        open={showCloseConfirm}
        onOpenChange={(open) => setShowCloseConfirm(open)}
      >
        <DialogContent className="w-[420px] max-w-[calc(100vw-48px)] gap-0 rounded-2xl border-0 bg-card p-0 shadow-[0_24px_70px_rgba(16,24,40,0.22)]">
          <DialogHeader className="px-7 pb-2 pt-7 text-left">
            <DialogTitle className="text-lg font-bold leading-tight text-foreground">
              종료하시겠습니까?
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-6 text-muted-foreground">
              생성 진행은 중단됩니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2 px-7 pb-7 pt-4">
            <Button
              type="button"
              onClick={() => setShowCloseConfirm(false)}
              className="h-11 min-w-[96px] rounded-full bg-white px-6 text-sm font-bold text-foreground ring-1 ring-inset ring-border hover:bg-light-muted"
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowCloseConfirm(false);
                onClose();
              }}
              className="h-11 min-w-[96px] rounded-full bg-sig px-6 text-sm font-bold text-white hover:bg-sig/90"
            >
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingResolution}
        onOpenChange={(open) => {
          if (!open && !isApplyingResolution) setPendingResolution(null);
        }}
      >
        <DialogContent className="w-[520px] max-w-[calc(100vw-48px)] gap-0 overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
          <DialogHeader className="border-b border-border px-7 py-6 text-left">
            <DialogTitle className="break-keep text-[21px] font-bold leading-tight text-foreground">
              이 방법으로 다시 생성할까요?
            </DialogTitle>
            <DialogDescription className="mt-1.5 break-keep text-sm leading-6 text-muted-foreground">
              선택한 조정값을 반영해 다시 생성합니다.
            </DialogDescription>
          </DialogHeader>

          {pendingResolution && (
            <div className="px-7 py-5">
              <div className="rounded-xl border border-border bg-light-muted px-4 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-bold text-foreground">
                    {pendingResolution.title_ko}
                  </p>
                  {pendingResolution.verified && (
                    <span className="inline-flex h-6 items-center rounded-full bg-sig/10 px-2.5 text-xs font-bold text-sig">
                      확인됨
                    </span>
                  )}
                </div>
                <p className="mt-2 break-keep text-sm leading-6 text-muted-foreground">
                  {pendingResolution.fix?.how_ko ||
                    pendingResolution.trade_off_ko ||
                    "선택한 조건을 반영해 다시 생성합니다."}
                </p>
                {(pendingResolution.changes?.length ?? 0) > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pendingResolution.changes?.map((change) => (
                      <span
                        key={`${pendingResolution.option_id}-${change.config_key}`}
                        className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm font-bold text-foreground ring-1 ring-inset ring-border"
                      >
                        {getChangeText(change)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-end gap-2 border-t border-border px-7 py-5">
            <Button
              type="button"
              variant="outline"
              className="h-11 min-w-[112px] rounded-full text-sm font-bold"
              onClick={() => setPendingResolution(null)}
              disabled={isApplyingResolution}
            >
              취소
            </Button>
            <Button
              type="button"
              className="h-11 min-w-[148px] rounded-full bg-sig px-6 text-sm font-bold text-white shadow-[0_10px_24px_rgba(0,114,255,0.24)] hover:bg-sig/90"
              aria-busy={isApplyingResolution}
              disabled={isApplyingResolution}
              onClick={async () => {
                if (!pendingResolution || isApplyingResolution) return;
                const option = pendingResolution;
                setIsApplyingResolution(true);
                try {
                  await onSelectResolution?.(option);
                  setPendingResolution(null);
                } finally {
                  setIsApplyingResolution(false);
                }
              }}
            >
              {isApplyingResolution ? (
                <>
                  <LoaderCircle className="size-4 animate-spin" aria-hidden />
                  재생성 요청 중
                </>
              ) : (
                "적용 후 재생성"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
